'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';

interface HeroSliderDiaaxProps {
  className?: string;
}

const HeroSliderDiaax = ({ className = '' }: HeroSliderDiaaxProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
    }

    const particles: Particle[] = [];
    const particleCount = 80;
    const connectionDistance = 150;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 2 + 1
      });
    }

    const animate = () => {
      ctx.fillStyle = 'rgba(15, 23, 42, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(20, 184, 166, 0.8)';
        ctx.fill();

        particles.forEach((otherParticle, j) => {
          if (i === j) return;

          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.3;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = `rgba(20, 184, 166, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <section className={`relative overflow-hidden ${className}`}>
      {/* Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: '#0f172a' }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/30 to-slate-900/60" />

      {/* Hero Content */}
      <div className="relative min-h-[70vh] sm:min-h-[80vh] md:h-[85vh] lg:h-[90vh] flex items-center justify-center py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-8 leading-[1.1] tracking-tight">
              Strategy & Capacity Building for Institutional Transformation
            </h1>

            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/95 mb-8 sm:mb-12 leading-relaxed font-light max-w-3xl mx-auto px-2">
              Diaax partners with governments, state-owned enterprises, and development organizations to bridge the critical gap between strategic vision and execution reality. We combine deep strategic thinking with practical capacity building to deliver long-term sustainable results.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center px-4 sm:px-0">
              <Link
                href="/contact"
                className="w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 bg-white text-slate-900 font-semibold rounded hover:bg-gray-100 transition-all duration-200 shadow-md hover:shadow-lg text-center text-sm sm:text-base tracking-wide"
              >
                Request Initial Conversation
              </Link>

              <Link
                href="/our-approach"
                className="w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 bg-transparent border-2 border-white text-white font-semibold rounded hover:bg-white/10 transition-all duration-200 text-center text-sm sm:text-base tracking-wide"
              >
                Our Approach
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSliderDiaax;